import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useIntl, type IntlShape} from 'react-intl';
import {
	Button,
	EMPHASIS,
	FlexColumn,
	FlexRow,
	Pill,
	PillVariant,
	SearchField,
	StackedList,
	SubHeading,
	Text,
	ToggleGroup,
} from '@forecast-it/design-system';
import InlineLoader from '../inline-loader/inline_loader';
import {AGENTIC_WORKERS_MESSAGE, describeAcceptLabel, describeStartedBy} from './agenticWorkersMessages';
import {
	AGENT_RESULT_STATE,
	AGENT_RUN_STATE,
	type AgentDeclaration,
	type AgentPastResult,
	type AgentPastRun,
	type AgentRecord,
	type AgentResultState,
	type AgentRunOutcome,
} from './agentDeclaration';
import {
	PAST_RUN_FILTER,
	describeFailureReason,
	useAgentPastRunsState,
	type AgentPastRunsQuery,
	type PastRunFilter,
} from './agentRunState';
import {formatPastRunTime, formatRunTime, useMinuteTick, type RunTimeMessages} from './agentRunTime';
import {AgentRecordSignifiers, KEY_SEPARATOR} from './AgentRecordSignifiers';

export const PAST_RUN_ROW_KIND = {
	RESULT: 'RESULT',
	FAILED: 'FAILED',
	NOTHING_FOUND: 'NOTHING_FOUND',
} as const;
export type PastRunRowKind = (typeof PAST_RUN_ROW_KIND)[keyof typeof PAST_RUN_ROW_KIND];

export const PAST_RUNS_SEARCH_DELAY_MILLISECONDS = 300;

// The row actions that leave the drawer open, so focus is handed on after them, and the done action it is handed to.
const PAST_RUN_ACTION = {
	ACCEPT: 'ACCEPT',
	DELETE: 'DELETE',
	DONE_ACTION: 'DONE_ACTION',
} as const;
type PastRunAction = (typeof PAST_RUN_ACTION)[keyof typeof PAST_RUN_ACTION];
type PastRunCommand = typeof PAST_RUN_ACTION.ACCEPT | typeof PAST_RUN_ACTION.DELETE;

// The result whose accept or Delete was pressed. Its part changes state, so its row takes a new key and mounts again:
// focus finds it by its result and the state it changes to.
type PressedResult = {
	runId: string;
	resultId: string;
	command: PastRunCommand;
};

const SHOW_MORE_MESSAGE = 'common.show-more';
const ACTED_TIME_MESSAGES: RunTimeMessages = {
	justNow: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_ACTED_JUST_NOW,
	at: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_ACTED_AT,
};
const NO_QUERY: AgentPastRunsQuery = {
	search: '',
	filter: PAST_RUN_FILTER.ALL,
};
// The order the server answers a result's parts in.
const PART_STATE_ORDER: AgentResultState[] = [
	AGENT_RESULT_STATE.NEEDS_REVIEW,
	AGENT_RESULT_STATE.DONE,
	AGENT_RESULT_STATE.DELETED,
];

type PastRunPill = {
	text: string;
	variant: PillVariant;
};

export type PastRunRow = {
	key: string;
	kind: PastRunRowKind;
	runId: string;
	result: AgentPastResult | null;
	startedByText: string;
	timeText: string;
	pill: PastRunPill | null;
	whoText: string | null;
	records: AgentRecord[];
	foundText: string;
};

type PastRunRowDetails = Pick<PastRunRow, 'kind' | 'result' | 'pill' | 'whoText' | 'records' | 'foundText'>;

type FilterOption = {
	filter: PastRunFilter;
	label: string;
	count: number | null;
};

// A row is named by its part, not its place, so it keeps its key when a re-read moves it.
const pastRunRowKey = (runId: string, ...rowNames: string[]): string => [runId, ...rowNames].join(KEY_SEPARATOR);

const actionButtonKey = (rowKey: string, action: PastRunAction): string => [rowKey, action].join(KEY_SEPARATOR);

// Focus that fell from an element taken away or disabled rests on the page's body, or on nothing at all.
const isFocusOnPage = (): boolean => document.activeElement === null || document.activeElement === document.body;

const EDITABLE_FIELD_SELECTOR = 'input, textarea, [contenteditable]:not([contenteditable="false"])';

const isFocusInEditableField = (): boolean =>
	document.activeElement !== null && document.activeElement.matches(EDITABLE_FIELD_SELECTOR);

// A part not acted on yet, such as a Delete in flight, is the latest act, and the re-read says who it was.
const lastActedOf = (first: AgentPastResult, second: AgentPastResult): Pick<AgentPastResult, 'actedByName' | 'actedAt'> => {
	if (first.actedAt === null || second.actedAt === null) {
		return {
			actedByName: null,
			actedAt: null,
		};
	}
	const lastActed = Date.parse(second.actedAt) > Date.parse(first.actedAt) ? second : first;
	return {
		actedByName: lastActed.actedByName,
		actedAt: lastActed.actedAt,
	};
};

const joinParts = (first: AgentPastResult, second: AgentPastResult): AgentPastResult => ({
	...first,
	rows: [...first.rows, ...second.rows],
	...lastActedOf(first, second),
});

// An accept or Delete shows on a result's Needs Review part before the re-read joins it with the result's part already
// in that state. Joining and ordering them here as the server does gives the rows the re-read's shape at once.
const joinResultParts = (parts: AgentPastResult[]): AgentPastResult[] =>
	Array.from(new Set(parts.map(part => part.id))).flatMap(resultId =>
		PART_STATE_ORDER.flatMap(state => {
			const partsInState = parts.filter(part => part.id === resultId && part.state === state);
			return partsInState.length === 0 ? [] : [partsInState.reduce(joinParts)];
		})
	);

const joinFoundTexts = (allFoundTexts: string[], {formatMessage}: IntlShape): string => {
	const foundTexts = allFoundTexts.filter(foundText => foundText !== '');
	if (foundTexts.length === 0) {
		return '';
	}
	return foundTexts.slice(1).reduce(
		(joined, foundText) =>
			formatMessage(
				{id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_FOUND_JOINED},
				{
					first: joined,
					second: foundText,
				}
			),
		foundTexts[0]
	);
};

// What the run says beyond its results, asked once per run.
const describeRunFoundTexts = (
	run: AgentPastRun,
	state: AgentRunOutcome['state'],
	declaration: AgentDeclaration,
	intl: IntlShape
): string[] =>
	declaration.foundText(
		{
			state,
			stateReason: run.stateReason,
			results: run.results,
			detail: run.detail,
		},
		intl
	);

const describePartPill = (part: AgentPastResult, declaration: AgentDeclaration, {formatMessage}: IntlShape): PastRunPill => {
	switch (part.state) {
		case AGENT_RESULT_STATE.NEEDS_REVIEW:
			return {
				text: formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_NEEDS_REVIEW}),
				variant: PillVariant.INFORMATION,
			};
		case AGENT_RESULT_STATE.DONE:
			return {
				text: formatMessage(declaration.doneStateName),
				variant: PillVariant.SUCCESS,
			};
		case AGENT_RESULT_STATE.DELETED:
			return {
				text: formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_DELETED}),
				variant: PillVariant.NEUTRAL,
			};
	}
};

// A Delete still in flight has no time yet, so it names nobody.
const describeWhoActed = (part: AgentPastResult, intl: IntlShape, now: Date): string | null => {
	if (part.state === AGENT_RESULT_STATE.NEEDS_REVIEW) {
		return intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_WAITING_FOR_YOU});
	}
	return formatRunTime(part.actedAt, now, intl, ACTED_TIME_MESSAGES, {
		name: part.actedByName ?? intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.TOAST_SOMEONE}),
	});
};

const toPastRunRow = (run: AgentPastRun, key: string, details: PastRunRowDetails, intl: IntlShape, now: Date): PastRunRow => ({
	key,
	runId: run.id,
	startedByText: describeStartedBy(run.startedBy, run.startedByName, intl),
	timeText: run.finishedAt === null ? '' : formatPastRunTime(run.finishedAt, now, intl),
	...details,
});

const buildRowsOfRun = (run: AgentPastRun, declaration: AgentDeclaration, intl: IntlShape, now: Date): PastRunRow[] => {
	switch (run.state) {
		case AGENT_RUN_STATE.PRODUCED: {
			const runFoundTexts = describeRunFoundTexts(run, run.state, declaration, intl);
			return joinResultParts(run.results).map(part => {
				const content = declaration.describeResult(part, intl);
				return toPastRunRow(
					run,
					pastRunRowKey(run.id, part.id, part.state),
					{
						kind: PAST_RUN_ROW_KIND.RESULT,
						result: part,
						pill: describePartPill(part, declaration, intl),
						whoText: describeWhoActed(part, intl, now),
						records: content.records,
						foundText: joinFoundTexts([content.foundText, ...runFoundTexts], intl),
					},
					intl,
					now
				);
			});
		}
		case AGENT_RUN_STATE.NOTHING_FOUND:
			return [
				toPastRunRow(
					run,
					pastRunRowKey(run.id, PAST_RUN_ROW_KIND.NOTHING_FOUND),
					{
						kind: PAST_RUN_ROW_KIND.NOTHING_FOUND,
						result: null,
						pill: null,
						whoText: null,
						records: [],
						foundText: joinFoundTexts(describeRunFoundTexts(run, run.state, declaration, intl), intl),
					},
					intl,
					now
				),
			];
		case AGENT_RUN_STATE.FAILED:
			return [
				toPastRunRow(
					run,
					pastRunRowKey(run.id, PAST_RUN_ROW_KIND.FAILED),
					{
						kind: PAST_RUN_ROW_KIND.FAILED,
						result: null,
						pill: {
							text: intl.formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_FAILED}),
							variant: PillVariant.NEUTRAL,
						},
						whoText: null,
						records: [],
						foundText: describeFailureReason(declaration, run, intl),
					},
					intl,
					now
				),
			];
		default:
			// A run in progress, a stopped run and a refused start never ended, so none has a row.
			return [];
	}
};

// The read is already searched and filtered, so every run and part in it gets its row, in the order read.
export const buildPastRunRows = (
	runs: AgentPastRun[],
	declaration: AgentDeclaration,
	intl: IntlShape,
	now: Date
): PastRunRow[] => runs.flatMap(run => buildRowsOfRun(run, declaration, intl, now));

// The count is blank until the first read answers.
const formatFilterCount = (filterOption: FilterOption, {formatNumber}: IntlShape): string | undefined =>
	filterOption.count === null ? undefined : formatNumber(filterOption.count);

// The read sends the search trimmed, so a search that differs only in spaces reads the same runs.
const isSameQuery = (first: AgentPastRunsQuery, second: AgentPastRunsQuery): boolean =>
	first.search.trim() === second.search.trim() && first.filter === second.filter;

const isFiltered = (query: AgentPastRunsQuery): boolean => query.search.trim() !== '' || query.filter !== PAST_RUN_FILTER.ALL;

// A lone action sits at the end of the records' line; several get a line of their own beneath it. Both end on the right.
type PastRunActions = {
	besideRecords: JSX.Element | null;
	belowRecords: JSX.Element | null;
};

const NO_PAST_RUN_ACTIONS: PastRunActions = {
	besideRecords: null,
	belowRecords: null,
};

type PastRunListItemProps = {
	row: PastRunRow;
	rowRef: (rowElement: HTMLLIElement | null) => void;
	actions: PastRunActions;
};

const PastRunListItem = ({row, rowRef, actions}: PastRunListItemProps): JSX.Element => {
	const hasRecordsLine = row.records.length > 0 || row.foundText !== '' || actions.besideRecords !== null;
	return (
		<StackedList.Item ref={rowRef} tabIndex={-1}>
			<FlexColumn gap={'s'}>
				<FlexRow alignItems={'flex-start'} justifyContent={'space-between'} gap={'m'}>
					<FlexColumn gap={'xxs'}>
						<SubHeading size={'2'}>{row.startedByText}</SubHeading>
						{row.timeText !== '' && (
							<Text size={'2'} color={'auxiliary'}>
								{row.timeText}
							</Text>
						)}
					</FlexColumn>
					{row.pill !== null && (
						<FlexColumn flex={'0 0 auto'} alignItems={'flex-end'} gap={'xxs'}>
							<Pill variant={row.pill.variant}>{row.pill.text}</Pill>
							{row.whoText !== null && (
								<Text size={'2'} color={'auxiliary'}>
									{row.whoText}
								</Text>
							)}
						</FlexColumn>
					)}
				</FlexRow>
				{hasRecordsLine && (
					<FlexRow
						alignItems={'center'}
						justifyContent={'space-between'}
						gap={'s'}
						data-cy={'agent-past-run-records'}
					>
						<FlexRow wrap={true} alignItems={'center'} gap={'xs'}>
							{row.records.length > 0 && <AgentRecordSignifiers records={row.records} />}
							{row.foundText !== '' && <Text size={'2'}>{row.foundText}</Text>}
						</FlexRow>
						{actions.besideRecords !== null && <FlexColumn flex={'0 0 auto'}>{actions.besideRecords}</FlexColumn>}
					</FlexRow>
				)}
				{actions.belowRecords}
			</FlexColumn>
		</StackedList.Item>
	);
};

// The result row focus was handed to, while focus stays in it.
type FollowedRow = {
	runId: string;
	resultId: string;
	rowKey: string;
};

type PastRunsFocus = {
	controlsRef: React.RefObject<HTMLDivElement>;
	listRef: React.RefObject<HTMLUListElement>;
	keepRowElement: (rowKey: string) => (rowElement: HTMLLIElement | null) => void;
	keepActionButton: (rowKey: string, action: PastRunAction) => (button: HTMLButtonElement | null) => void;
	notePressedResult: (pressedResult: PressedResult) => void;
};

// Where focus goes in the list: on after an accept or Delete, on again when a re-read takes the focused row away, and to
// an asked-for run. It never falls to the page, and never leaves a field someone is typing in.
const usePastRunsFocus = (
	rows: PastRunRow[],
	acceptingResultIds: ReadonlySet<string>,
	focusRunId: string | null
): PastRunsFocus => {
	const controlsRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const rowElementsRef = useRef(new Map<string, HTMLLIElement>());
	const actionButtonsRef = useRef(new Map<string, HTMLButtonElement>());
	const pressedResultRef = useRef<PressedResult | null>(null);
	const followedRowRef = useRef<FollowedRow | null>(null);
	const broughtIntoViewRunIdRef = useRef<string | null>(null);

	// Another of the result's rows takes focus, else the list: never whatever row now sits where the focused one was.
	const focusRowOfResult = (runId: string, resultId: string): void => {
		const resultRow = rows.find(row => row.runId === runId && row.result !== null && row.result.id === resultId);
		const resultRowElement = resultRow === undefined ? undefined : rowElementsRef.current.get(resultRow.key);
		if (resultRow === undefined || resultRowElement === undefined) {
			followedRowRef.current = null;
			listRef.current?.focus();
			return;
		}
		resultRowElement.focus();
		followedRowRef.current = {
			runId,
			resultId,
			rowKey: resultRow.key,
		};
	};

	// The result's row in that state, or one of its actions, takes focus, and the row is followed from then on.
	const focusResultRow = (runId: string, resultId: string, state: AgentResultState, action: PastRunAction | null): void => {
		const rowKey = pastRunRowKey(runId, resultId, state);
		const target =
			action === null
				? rowElementsRef.current.get(rowKey)
				: actionButtonsRef.current.get(actionButtonKey(rowKey, action));
		if (target === undefined) {
			focusRowOfResult(runId, resultId);
			return;
		}
		target.focus();
		followedRowRef.current = {
			runId,
			resultId,
			rowKey,
		};
	};

	// The store turns an applied accept's part Done in the same publish that ends the wait, so a part still waiting once
	// the accept answered means it did not apply.
	const handFocusOnAfterAccept = ({runId, resultId}: PressedResult): void => {
		if (rowElementsRef.current.has(pastRunRowKey(runId, resultId, AGENT_RESULT_STATE.NEEDS_REVIEW))) {
			focusResultRow(runId, resultId, AGENT_RESULT_STATE.NEEDS_REVIEW, PAST_RUN_ACTION.ACCEPT);
		} else {
			focusResultRow(runId, resultId, AGENT_RESULT_STATE.DONE, PAST_RUN_ACTION.DONE_ACTION);
		}
	};

	// The search box focuses itself as it mounts, and the drawer leaves it there. For an asked-for run it gives that up,
	// so the drawer takes focus and the run's row can take it once read: no one is typing in the search box yet. Read
	// once, as a new asked-for run mounts the past runs again.
	useEffect(() => {
		const focused = document.activeElement;
		if (focusRunId !== null && focused instanceof HTMLElement && controlsRef.current?.contains(focused)) {
			focused.blur();
		}
	}, []);

	// A pressed accept or Delete changes its row's key, so the row it leaves takes its focus with it. Focus is handed on
	// after each render, once the row reads its new state.
	useEffect(() => {
		const pressedResult = pressedResultRef.current;
		if (pressedResult !== null && pressedResult.command === PAST_RUN_ACTION.DELETE) {
			// A Delete shows at once: its part reads Deleted, with no actions left to hold focus.
			pressedResultRef.current = null;
			focusResultRow(pressedResult.runId, pressedResult.resultId, AGENT_RESULT_STATE.DELETED, null);
		} else if (pressedResult !== null && !acceptingResultIds.has(pressedResult.resultId)) {
			pressedResultRef.current = null;
			// The loader disables the button, so a browser drops focus to the page. Focus that moved on stays where it is.
			if (isFocusOnPage()) {
				handFocusOnAfterAccept(pressedResult);
			}
		}
		const followedRow = followedRowRef.current;
		const followedRowElement = followedRow === null ? undefined : rowElementsRef.current.get(followedRow.rowKey);
		if (followedRow !== null && followedRowElement === undefined) {
			// A re-read took the row away, and its focus with it.
			followedRowRef.current = null;
			if (isFocusOnPage()) {
				focusRowOfResult(followedRow.runId, followedRow.resultId);
			}
		} else if (followedRowElement !== undefined && !followedRowElement.contains(document.activeElement)) {
			followedRowRef.current = null;
		}
	});

	// The asked-for run is brought into view once, when its first row shows, which can take more than one read. Someone
	// typing by then keeps their place in the field.
	useEffect(() => {
		if (focusRunId === null || focusRunId === broughtIntoViewRunIdRef.current) {
			return;
		}
		const firstRow = rows.find(row => row.runId === focusRunId);
		const firstRowElement = firstRow === undefined ? undefined : rowElementsRef.current.get(firstRow.key);
		if (firstRowElement !== undefined) {
			broughtIntoViewRunIdRef.current = focusRunId;
			firstRowElement.scrollIntoView({block: 'nearest'});
			if (!isFocusInEditableField()) {
				firstRowElement.focus();
			}
		}
	}, [focusRunId, rows]);

	return {
		controlsRef,
		listRef,
		keepRowElement: rowKey => rowElement => {
			if (rowElement) {
				rowElementsRef.current.set(rowKey, rowElement);
			} else {
				rowElementsRef.current.delete(rowKey);
			}
		},
		keepActionButton: (rowKey, action) => button => {
			const key = actionButtonKey(rowKey, action);
			if (button) {
				actionButtonsRef.current.set(key, button);
			} else {
				actionButtonsRef.current.delete(key);
			}
		},
		notePressedResult: pressedResult => {
			pressedResultRef.current = pressedResult;
		},
	};
};

type AgentPastRunsProps = {
	declaration: AgentDeclaration;
	// The run to bring into view once the read lists it.
	focusRunId: string | null;
	onClose: () => void;
};

// One agent's past runs, as the drawer shows them. The search waits for typing to stop; a state filter is read at once.
export const AgentPastRuns = ({declaration, focusRunId, onClose}: AgentPastRunsProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {
		pastRuns,
		counts,
		query,
		canShowMore,
		isReadingMore,
		acceptingResultIds,
		review,
		accept,
		deleteResult,
		setQuery,
		showMore,
	} = useAgentPastRunsState(declaration);
	const [searchText, setSearchText] = useState('');
	const [filter, setFilter] = useState<PastRunFilter>(PAST_RUN_FILTER.ALL);
	const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// What the store reads for, which a read in flight has not answered yet. The store starts each drawer with no query.
	const handedQueryRef = useRef<AgentPastRunsQuery>(NO_QUERY);
	const minuteTicks = useMinuteTick(true);
	const rows = useMemo(
		() => (pastRuns === null ? [] : buildPastRunRows(pastRuns, declaration, intl, new Date())),
		// The minute ticks keep the times moving.
		[pastRuns, declaration, intl, minuteTicks]
	);

	const {controlsRef, listRef, keepRowElement, keepActionButton, notePressedResult} = usePastRunsFocus(
		rows,
		acceptingResultIds,
		focusRunId
	);

	const cancelPendingSearch = (): void => {
		if (searchTimerRef.current !== null) {
			clearTimeout(searchTimerRef.current);
			searchTimerRef.current = null;
		}
	};

	// A search still waiting would otherwise land in the next drawer's read.
	useEffect(() => cancelPendingSearch, []);

	// A new query reads again from the first page, so an unchanged one is not handed over and keeps what Show more loaded.
	const handOver = (nextQuery: AgentPastRunsQuery): void => {
		if (isSameQuery(nextQuery, handedQueryRef.current)) {
			return;
		}
		handedQueryRef.current = nextQuery;
		setQuery(nextQuery);
	};

	const changeSearch = (nextSearchText: string): void => {
		setSearchText(nextSearchText);
		cancelPendingSearch();
		searchTimerRef.current = setTimeout(() => {
			searchTimerRef.current = null;
			handOver({
				search: nextSearchText,
				filter,
			});
		}, PAST_RUNS_SEARCH_DELAY_MILLISECONDS);
	};

	const selectFilter = (nextFilter: PastRunFilter): void => {
		cancelPendingSearch();
		setFilter(nextFilter);
		handOver({
			search: searchText,
			filter: nextFilter,
		});
	};

	const clearFilters = (): void => {
		cancelPendingSearch();
		setSearchText(NO_QUERY.search);
		setFilter(NO_QUERY.filter);
		handOver(NO_QUERY);
	};

	const resultCommands: Record<PastRunCommand, (result: AgentPastResult) => void> = {
		[PAST_RUN_ACTION.ACCEPT]: accept,
		[PAST_RUN_ACTION.DELETE]: deleteResult,
	};

	const pressResultCommand = (row: PastRunRow, part: AgentPastResult, command: PastRunCommand): void => {
		notePressedResult({
			runId: row.runId,
			resultId: part.id,
			command,
		});
		resultCommands[command](part);
	};

	// Review and the done action leave the drawer for what the agent opens.
	const pressReview = (part: AgentPastResult): void => {
		onClose();
		review(part);
	};

	const pressDoneAction = (part: AgentPastResult): void => {
		onClose();
		declaration.doneAction.perform(part);
	};

	const reviewLabel = formatMessage({id: AGENTIC_WORKERS_MESSAGE.RESULT_REVIEW});
	const deleteLabel = formatMessage({id: AGENTIC_WORKERS_MESSAGE.RESULT_DELETE});
	const doneActionLabel = formatMessage(declaration.doneAction.label);

	// A result's parts share its id, and only the part still in review has an accept to wait on. While it waits, the
	// part's other actions wait with it, so a Delete never races the accept.
	const renderActions = (row: PastRunRow): PastRunActions => {
		const part = row.result;
		if (part === null) {
			return NO_PAST_RUN_ACTIONS;
		}
		switch (part.state) {
			case AGENT_RESULT_STATE.NEEDS_REVIEW: {
				const isAccepting = acceptingResultIds.has(part.id);
				return {
					besideRecords: null,
					belowRecords: (
						<FlexRow justifyContent={'flex-end'} gap={'xs'}>
							<Button emphasis={EMPHASIS.LOW} disabled={isAccepting} onPress={() => pressReview(part)}>
								{reviewLabel}
							</Button>
							<Button
								ref={keepActionButton(row.key, PAST_RUN_ACTION.ACCEPT)}
								emphasis={EMPHASIS.MEDIUM}
								waiting={isAccepting}
								onPress={() => pressResultCommand(row, part, PAST_RUN_ACTION.ACCEPT)}
							>
								{describeAcceptLabel(declaration, part, intl)}
							</Button>
							<Button
								emphasis={EMPHASIS.LOW}
								disabled={isAccepting}
								onPress={() => pressResultCommand(row, part, PAST_RUN_ACTION.DELETE)}
							>
								{deleteLabel}
							</Button>
						</FlexRow>
					),
				};
			}
			case AGENT_RESULT_STATE.DONE:
				return {
					besideRecords: (
						<Button
							ref={keepActionButton(row.key, PAST_RUN_ACTION.DONE_ACTION)}
							emphasis={EMPHASIS.LOW}
							onPress={() => pressDoneAction(part)}
						>
							{doneActionLabel}
						</Button>
					),
					belowRecords: null,
				};
			case AGENT_RESULT_STATE.DELETED:
				return NO_PAST_RUN_ACTIONS;
		}
	};

	const filterOptions: FilterOption[] = [
		{
			filter: PAST_RUN_FILTER.ALL,
			label: formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_ALL}),
			count: counts?.all ?? null,
		},
		{
			filter: PAST_RUN_FILTER.NEEDS_REVIEW,
			label: formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_NEEDS_REVIEW}),
			count: counts?.needsReview ?? null,
		},
		{
			filter: PAST_RUN_FILTER.DONE,
			label: formatMessage(declaration.doneStateName),
			count: counts?.done ?? null,
		},
		{
			filter: PAST_RUN_FILTER.DELETED,
			label: formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_DELETED}),
			count: counts?.deleted ?? null,
		},
	];
	// Pressing the selected option again answers no option, which would leave none selected, so it changes nothing.
	const changeFilter = (value: string | string[]): void => {
		const nextFilter = filterOptions.find(filterOption => filterOption.filter === value)?.filter;
		if (nextFilter !== undefined) {
			selectFilter(nextFilter);
		}
	};
	const searchLabel = formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_SEARCH});
	const isNothingMatching = pastRuns !== null && pastRuns.length === 0 && isFiltered(query);

	// The search box and the state filter stay put while the list scrolls under them.
	return (
		<StackedList
			headerRef={controlsRef}
			header={
				<FlexColumn gap={'s'}>
					<SearchField
						isExpandable={false}
						width={'100%'}
						placeholder={searchLabel}
						aria-label={searchLabel}
						value={searchText}
						onChange={changeSearch}
						data-cy={'agent-past-runs-search'}
					/>
					{/* The toggle group ends its options on the right; as a row item it is only as wide as its options, so they start on the left. */}
					<FlexRow>
						<ToggleGroup
							aria-label={formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_FILTER_LABEL})}
							type={'single'}
							notBold={true}
							value={filter}
							onValueChange={changeFilter}
							options={filterOptions.map(filterOption => ({
								value: filterOption.filter,
								label: filterOption.label,
								count: formatFilterCount(filterOption, intl),
								disabled: false,
							}))}
						/>
					</FlexRow>
				</FlexColumn>
			}
		>
			{pastRuns === null ? (
				<InlineLoader />
			) : (
				<FlexColumn gap={'m'}>
					<StackedList.Items ref={listRef} tabIndex={-1}>
						{rows.map(row => (
							<PastRunListItem
								key={row.key}
								row={row}
								rowRef={keepRowElement(row.key)}
								actions={renderActions(row)}
							/>
						))}
					</StackedList.Items>
					{(isNothingMatching || canShowMore) && (
						<FlexColumn gap={'s'} alignItems={'flex-start'}>
							{isNothingMatching && (
								<>
									<Text size={'2'} color={'auxiliary'}>
										{formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_NO_MATCH})}
									</Text>
									<Button emphasis={EMPHASIS.LOW} onPress={clearFilters}>
										{formatMessage({id: AGENTIC_WORKERS_MESSAGE.PAST_RUNS_CLEAR_FILTERS})}
									</Button>
								</>
							)}
							{canShowMore && (
								<Button emphasis={EMPHASIS.LOW} waiting={isReadingMore} onPress={showMore}>
									{formatMessage({id: SHOW_MORE_MESSAGE})}
								</Button>
							)}
						</FlexColumn>
					)}
				</FlexColumn>
			)}
		</StackedList>
	);
};
